/*
 * pageReturn — where the header Back button goes, and the scroll it lands on
 * (2026-10-12 owner ask).
 *
 * THE RULE. A detail page opened from its own list goes Back to that list:
 * a species page opened from the Dex returns to the Dex grid, a record page
 * opened from My Pokémon returns to the My Pokémon list, each restoring the
 * row the trainer left (dexScrollRestore / myPokemonScrollRestore). That is
 * the behaviour the owner asked to keep. A detail page opened from ANYWHERE
 * ELSE (the Rocket page, the Profile, a Calendar card, another detail) goes
 * Back to the page it was opened from, scrolled to where that page was.
 * Before this module every record page sent Back to the list and every
 * species page to the Dex, so a Rocket counter tapped into My Pokémon came
 * Back out on the My Pokémon list, two pages away from where the trainer
 * was reading.
 *
 * And the same promise for every other page: Back is the previous page, at
 * the offset it was left at, not the previous page at the top.
 *
 * HOW. Three pieces of module state, kept here because the pages involved
 * are unmounted while the detail page is open (the same reason the two
 * scroll-restore modules live outside React):
 *
 *   1. The path the trainer is on and the path before it, tracked off the
 *      hash router, and the last known page offset of EVERY path, recorded
 *      from scroll events and again at the moment a navigation fires (the
 *      old DOM is still mounted then, so the read is the old page's).
 *   2. The ORIGIN of a detail excursion: on entering a /pokemon/ or
 *      /my-pokemon/ page from outside that family's home, the page and
 *      offset it came from. Stepping sideways between details keeps it;
 *      entering from the family's home sets it to null (the list rule);
 *      leaving the family drops it.
 *   3. A PENDING RESTORE: Back arms `{ path, scrollY }`, and the route reset
 *      hook (useScrollResetOnRoute) stands down for that one arrival while a
 *      settle loop puts the offset back. Keyed by path so a navigation that
 *      lands anywhere else simply discards it.
 *
 * The Battle Hub's own return intent (battleHubMemory) is older and more
 * specific (it restores the hub's mode and filters too); the shell consults
 * it first, so nothing here changes that round-trip.
 */
import { hashGetSnapshot, hashSubscribe } from "./hashRouter";
import { getPageScrollTop, onPageScroll } from "./pageScroll";

/** A page and the offset to put it back at. */
export interface PageReturnTarget {
  path: string;
  scrollY: number;
}

/**
 * The two detail families and what counts as each one's HOME: the surface
 * whose own scroll restore owns the return, where Back keeps its list rule.
 */
interface DetailFamily {
  prefix: string;
  isHome: (path: string) => boolean;
}

const FAMILIES: readonly DetailFamily[] = [
  {
    prefix: "/pokemon/",
    isHome: (p) => p === "/dex" || p === "/pokedex" || p.startsWith("/dex/"),
  },
  {
    prefix: "/my-pokemon/",
    isHome: (p) => p === "/my-pokemon",
  },
];

function familyOf(path: string | null): DetailFamily | null {
  if (!path) return null;
  return FAMILIES.find((f) => path.startsWith(f.prefix)) ?? null;
}

function safeSnapshot(): string | null {
  try {
    return hashGetSnapshot();
  } catch {
    return null;
  }
}

let currentPath: string | null = typeof window !== "undefined" ? safeSnapshot() : null;
let previousPath: string | null = null;
/** Last known page offset per path, as of the last scroll event on it. */
const scrollByPath = new Map<string, number>();
/** Where the current detail excursion began, or null for "its own list". */
let origin: PageReturnTarget | null = null;
let originFamily: DetailFamily | null = null;
/** Armed by Back; consumed by the arriving page's restore. */
let pending: PageReturnTarget | null = null;
/**
 * The restore most recently taken, held until the next navigation. The
 * restore runs in a LAYOUT effect (pre-paint) and the route reset in a
 * passive one, so by the time the reset asks whether to stand down the
 * target has already been consumed; this is how it still gets a yes.
 */
let taken: PageReturnTarget | null = null;

function clamp(y: number): number {
  return Math.max(0, Math.round(Number.isFinite(y) ? y : 0));
}

function recordScroll(path: string | null, y: number): void {
  if (!path) return;
  scrollByPath.set(path, clamp(y));
}

/** The navigation hook: runs on every hashchange / popstate. */
function onNavigate(next: string | null, scrollAtLeave: number): void {
  // hashchange AND popstate can both fire for one navigation; ignore the
  // repeat so previousPath never collapses onto the current route.
  if (next === currentPath) return;
  // The DOM that is still mounted at this moment is the OLD page's, so this
  // read is its offset: the freshest value we will get for it.
  recordScroll(currentPath, scrollAtLeave);
  previousPath = currentPath;
  currentPath = next;
  taken = null;

  const nextFamily = familyOf(next);
  if (nextFamily) {
    const prevFamily = familyOf(previousPath);
    if (prevFamily === nextFamily) {
      // Sideways step inside the excursion (a swipe to the next record or
      // species): the origin stands.
      return;
    }
    if (previousPath && nextFamily.isHome(previousPath)) {
      // Opened from its own list: Back means the list (dexScrollRestore /
      // myPokemonScrollRestore restore the row).
      origin = null;
      originFamily = nextFamily;
      return;
    }
    // Opened from somewhere else, including a detail of the OTHER family:
    // Back means that page, at the offset it was left at.
    origin = previousPath
      ? { path: previousPath, scrollY: scrollByPath.get(previousPath) ?? 0 }
      : null;
    originFamily = nextFamily;
    return;
  }
  // Anywhere outside a detail family ends the excursion.
  origin = null;
  originFamily = null;
}

if (typeof window !== "undefined") {
  onPageScroll(() => recordScroll(currentPath, getPageScrollTop()));
  hashSubscribe(() => onNavigate(safeSnapshot(), getPageScrollTop()));
}

/** The route the trainer is on, as this module last saw it. */
export function currentPagePath(): string | null {
  return currentPath;
}

/**
 * The page before the current one, with its last offset: what Back means on
 * a page that is not a detail of either family. Null on the first page of
 * the session.
 */
export function previousPage(): PageReturnTarget | null {
  if (!previousPath) return null;
  return { path: previousPath, scrollY: scrollByPath.get(previousPath) ?? 0 };
}

/**
 * Where Back should go from the detail page at `path`, when the page was
 * opened from somewhere OTHER than its own list. Null means "its own list":
 * the caller keeps the list rule.
 */
export function detailReturnTarget(path: string): PageReturnTarget | null {
  const family = familyOf(path);
  if (!family || family !== originFamily) return null;
  return origin ? { ...origin } : null;
}

/**
 * Arm the arriving page's offset. Called by Back right before it navigates;
 * the route reset hook stands down for a matching arrival and the restore
 * hook settles the offset.
 */
export function armPageReturn(target: PageReturnTarget): void {
  pending = { path: target.path, scrollY: clamp(target.scrollY) };
}

/**
 * True when `path` is the page a Back navigation is landing on with an
 * offset to restore: armed and not yet taken, or taken during this same
 * arrival. The route reset hook stands down on a yes.
 */
export function pageReturnPending(path: string): boolean {
  return (pending != null && pending.path === path) || (taken != null && taken.path === path);
}

/**
 * Take the pending restore for `path`, if that is where the navigation
 * landed. A pending restore for any OTHER path is dropped here too: it
 * described a navigation that did not happen.
 */
export function takePageReturn(path: string): PageReturnTarget | null {
  const out = pending;
  pending = null;
  taken = out && out.path === path ? out : null;
  return taken;
}

/** Test seam. */
export function __resetPageReturnForTest(prev: string | null, curr: string | null): void {
  previousPath = prev;
  currentPath = curr;
  scrollByPath.clear();
  origin = null;
  originFamily = null;
  pending = null;
  taken = null;
}

/** Test seam: drive the navigation hook without a real hash change. */
export function __navigateForTest(next: string, scrollAtLeave = 0): void {
  onNavigate(next, scrollAtLeave);
}
